import { IUserInfoDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { UserService } from "../../../services/user-service";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import {
  KuikaSkeletonDetail,
  KuikaSkeletonList,
  KuikaSkeletonRegion
} from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import { ProfileUserInfo } from "./profile-user-info";
import { ProfileUserProjects } from "./profile-user-projects";

declare let window: any;
export interface IMemberInfoState {
  isChangePasswordModalActive: boolean;
  user: IUserInfoDto;
  isLoading: boolean;
}

export interface IMemberInfoProps {
  closeModal: () => void;
  showModal: boolean;
  userId: string;
  updateUserList: () => void;
}

export enum EMemberInfoType {
  OWNER = 1,
  USER = 2
}

class MemberInfo extends React.PureComponent<IMemberInfoProps & IWithHistoryProp & IWithContextProp, IMemberInfoState> {
  constructor(props: IMemberInfoProps & IWithHistoryProp & IWithContextProp) {
    super(props);
    this.state = {
      isChangePasswordModalActive: false,
      user: {} as IUserInfoDto,
      isLoading: false
    };
  }

  async componentDidMount() {
    this.handleGetUserById();
  }

  handleGetUserById = async () => {
    if (this.props.userId) {
      this.setState({ isLoading: true });
      try {
        const res: any = await UserService.GetUserById(this.props.userId);
        if (res) {
          this.setState({ user: res });
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      } finally {
        this.setState({ isLoading: false });
      }
    }
  };

  /**
   * The member's two panels in outline, while the first fetch has nothing to show: their identity
   * rail on the left, their apps on the right. A re-read after a role change keeps the member on
   * screen instead — it used to hide the whole body behind a spinner for it.
   */
  renderPlaceholder = (): React.ReactNode => (
    <div className="kuika_profile kuika_profile__member">
      <KuikaSkeletonRegion label="Loading member">
        <div className="kuika_profile__split">
          <div className="kuika_profile__user-infobox">
            <KuikaSkeletonDetail rows={4} />
          </div>
          <div className="kuika_profile__pane">
            <KuikaSkeletonList rows={6} withIcon />
          </div>
        </div>
      </KuikaSkeletonRegion>
    </div>
  );

  render() {
    const userInfo: IUserInfoDto = this.state.user ?? this.props.context?.userInfo;
    const isCurrentUserOwner: boolean = this.props.context?.userInfo.isDomainOwner;
    const isPlaceholder: boolean =
      resolveLoadingPresentation(this.state.isLoading, !!this.state.user?.id) === "skeleton";
    return (
      <KModal
        className="kuika_profile__modal"
        header={<span data-testid="memberInfoModalTitle">Member info</span>}
        closeTestId="memberInfoModalCloseButton"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        width={720}
      >
        {isPlaceholder ? (
          this.renderPlaceholder()
        ) : (
          <div className="kuika_profile kuika_profile__member">
            {!KMainFunctions.isDomainAuthorizationEnabled() && (
              <div className="kuika_profile__auth-off-notice" data-testid="memberInfoAuthOffNotice">
                Workspace authorization is off — project roles below are saved but do not restrict access until you
                enable it.
              </div>
            )}
            <div className="kuika_profile__split">
              <ProfileUserInfo
                userInfo={userInfo}
                type={isCurrentUserOwner ? EMemberInfoType.OWNER : EMemberInfoType.USER}
                handleGetUserById={this.handleGetUserById}
                updateUserList={this.props.updateUserList}
              />
              <div className="kuika_profile__pane">
                {/* No enrich here: GetMyProjects returns the viewer's apps,
                  not this member's, so the icons and screen ids would
                  belong to the wrong person. */}
                <ProfileUserProjects
                  profileInfo={userInfo}
                  type={isCurrentUserOwner ? EMemberInfoType.OWNER : EMemberInfoType.USER}
                />
              </div>
            </div>
          </div>
        )}
      </KModal>
    );
  }
}

const memberInfo = withContext<IMemberInfoProps>(
  withHistory<IMemberInfoProps & IWithHistoryProp & IWithContextProp>(MemberInfo)
);
export { memberInfo as MemberInfo };
